import {Injectable} from '@angular/core';
import {
    AndConstraint,
    ComparisonConstraint,
    Constraint,
    DeltaDateConstraint,
    FieldOrder,
    OrConstraint,
    Ordering,
    PropertyExistenceConstraint,
    PropertyValue,
    SaveQuery,
    Value
} from '@tj/tj-dmp-lib-api';
import {
    CampaignFilterType,
    DateFilterType,
    Filter,
    isEmptinessFilterType,
    ListFilterType,
    NewsletterFilterType,
    NumberFilterType,
    StringFilterType
} from '../components/filter/filter.type';
import {SortMeta, TreeNode} from 'primeng/api';
import {ClassicTreeSave} from '../components/filter/filter-preset/filter-preset.component';
import { SubQueryConstraint } from 'projects/tj/dmp-lib-api/src/model/subQueryConstraint';
import { SlidingDateRangeConstraint } from 'projects/tj/dmp-lib-api/src/model/slidingDateRangeConstraint';

@Injectable({
    providedIn: 'root',
})
export class FilterBuilderService {

    public buildQuery(table: string, filters: Filter[]): AndConstraint;
    public buildQuery(filters: Filter[]): AndConstraint;
    public buildQuery(arg0: Filter[] | string, arg1?: Filter[]): AndConstraint {
        const filters = typeof arg0 === 'string' ? arg1 : arg0;
        const table = typeof arg0 === 'string' ? arg0 : undefined;
        if (!filters || filters.length === 0) {
            return null;
        }
        const query = {
            '@type': 'AndConstraint',
            constraints: filters
                .map((filter) => this.getOperatorFromType(`${table ?? filter.schema}.${filter.field.name}`, filter.field.variableType, filter.filterType, filter.value, filter.options))
                .filter(constraint => constraint != null)
        };
        return query;
    }

    buildTreeQuery($event: TreeNode<Filter>): Constraint {
        if ($event) {
            return this.getContraint($event);
        } else {
            return null;
        }
    }

    private getContraint(node: TreeNode<Filter>): Constraint {
        const filter = node.data;
        const typesWithoutValue = [
            NewsletterFilterType.SUBSCRIBED_TO_ANY,
            NewsletterFilterType.NOT_SUBSCRIBED_TO_ANY,
            CampaignFilterType.IMPORT,
            CampaignFilterType.EXPORT
        ];
        const isEmpty = filter.value == null ||
            filter.value instanceof Array && (filter.value as Array<any>).length === 0 ||
            typeof filter.value === 'string' && (filter.value as string).trim().length === 0;
        let query = null;
        if (filter && ['OR', 'AND'].includes(filter.typeGroup) && node.children) {
            if (filter.typeGroup === 'OR') {
                query = {
                    '@type': 'OrConstraint',
                    constraints: []
                };
            } else {
                query = {
                    '@type': 'AndConstraint',
                    constraints: []
                };
            }
            query.constraints = node.children
                ?.map(item => this.getContraint(item))
                .filter(item => item != null);
        } else if (filter && filter.filterType && filter.filterType.code && !isEmptinessFilterType(filter.filterType.code)
            && !typesWithoutValue.includes(filter.filterType.code as any) && isEmpty) {
            query = null;
        } else if (filter) {
            query = this.getOperatorFromType(`${filter.field.schema}.${filter.field.name}`, filter.field.variableType as 'string' | 'number' | 'boolean' | 'date' | 'list' | 'newsletter' | 'campaign', 
                filter.filterType, filter.value, filter.options);
        }
        return query;
    }


    private parseOperatorToConstraint(propertyName: string,
                                      operator: [unknown[], ComparisonConstraint.OperatorEnum, string], options?: any): Constraint {
        let optionsValue = null;
        if (options) {
            optionsValue = {
                excludeYear: options.excludeYear === true
            };
        }
        return {
            '@type': 'OrConstraint',
            constraints: operator[0].map(value => ({
                '@type': 'ComparisonConstraint',
                operand1: {
                    '@type': 'PropertyValue',
                    propertyName,
                    options: optionsValue
                } as PropertyValue,
                operand2: {
                    '@type': 'Value',
                    typeValue: operator[2],
                    value
                } as Value,
                operator: operator[1]
            } as ComparisonConstraint))
        } as OrConstraint;
    }


    /**
     * « Est vide » / « N'est pas vide » pour un champ où vide et null sont équivalents (texte, date, liste) :
     * - ISNULL : nom_du_champ IS NULL OR nom_du_champ = ''
     * - ISNOTNULL : nom_du_champ IS NOT NULL AND nom_du_champ <> ''
     *
     * @param propertyName Nom du champ à tester
     * @param operator tableau de deux éléments [ISNULL / ISNOTNULL, type de la valeur comparée]
     */
    private parseOperatorToExistenceConstraint(propertyName: string, operator: [PropertyExistenceConstraint.ExistenceOperatorEnum, string]): Constraint {
        const isNull = operator[0] === PropertyExistenceConstraint.ExistenceOperatorEnum.ISNULL;
        const constraints: Constraint[] = [
            this.existence(propertyName, operator[0]),
            {
                '@type': 'ComparisonConstraint',
                operand1: {
                    '@type': 'PropertyValue',
                    propertyName
                } as PropertyValue,
                operand2: {
                    '@type': 'Value',
                    typeValue: operator[1],
                    value: ''
                } as Value,
                operator: isNull ? ComparisonConstraint.OperatorEnum.EQUALS : ComparisonConstraint.OperatorEnum.NOTEQUALS
            } as ComparisonConstraint
        ];
        return isNull ? this.or(constraints) : this.and(constraints);
    }

    private applyOperatorToString(value: unknown[] | unknown, comparator: (value: unknown) => unknown = (e) => e) {
        value = this.getValueFromObjectOrList(value);
        const arrayValue = Array.isArray(value) ? value : [value];
        return arrayValue.map(comparator);
    }

    private getOperatorFromType(propertyName: string, variableType: 'string' | 'number' | 'boolean' | 'date' | 'list' | 'newsletter' | 'campaign', comparatorType: {
        code: unknown,
        value: unknown
    }, value: unknown, options?: any): Constraint {
        switch (variableType) {
            case 'string':
                switch (comparatorType.code) {
                    case StringFilterType.EXACTLY:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.EQUALS, 'STRING']);
                    case StringFilterType.NOT_EXACTLY:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.NOTEQUALS, 'STRING']);
                    case StringFilterType.CONTAINS:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value, (value) => '%' + value + '%'), ComparisonConstraint.OperatorEnum.LIKE, 'STRING']);
                    case StringFilterType.EMPTY:
                        return this.parseOperatorToExistenceConstraint(propertyName, [PropertyExistenceConstraint.ExistenceOperatorEnum.ISNULL, 'STRING']);
                    case StringFilterType.NOT_EMPTY:
                        return this.parseOperatorToExistenceConstraint(propertyName, [PropertyExistenceConstraint.ExistenceOperatorEnum.ISNOTNULL, 'STRING']);
                    case StringFilterType.ENDS_WITH:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value, (value) => '%' + value), ComparisonConstraint.OperatorEnum.LIKE, 'STRING']);
                    case StringFilterType.STARTS_WITH:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value, (value) => value + '%'), ComparisonConstraint.OperatorEnum.LIKE, 'STRING']);
                    case StringFilterType.NOT_STARTS_WITH:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value, (value) => value + '%'), ComparisonConstraint.OperatorEnum.NOTLIKE, 'STRING']);
                    case StringFilterType.NOT_CONTAINS:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value, (value) => '%' + value + '%'), ComparisonConstraint.OperatorEnum.NOTLIKE, 'STRING']);
                }
                break;
            case 'number':
                switch (comparatorType.code) {
                    case NumberFilterType.EMPTY:
                        // IS NULL seul : sur une colonne numérique, = '' vaudrait = 0
                        return this.existence(propertyName, PropertyExistenceConstraint.ExistenceOperatorEnum.ISNULL);
                    case NumberFilterType.NOT_EMPTY:
                        return this.existence(propertyName, PropertyExistenceConstraint.ExistenceOperatorEnum.ISNOTNULL);
                    case NumberFilterType.EQUALS:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.EQUALS, 'LONG']);
                    case NumberFilterType.NOT_EXACTLY:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.NOTEQUALS, 'LONG']);
                    case NumberFilterType.GREATER:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.GREATERTHAN, 'LONG']);
                    case NumberFilterType.GREATER_EQUALS:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.GREATERTHANOREQUAL, 'LONG']);
                    case NumberFilterType.LESS:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.LESSTHAN, 'LONG']);
                    case NumberFilterType.LESS_EQUALS:
                        return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.LESSTHANOREQUAL, 'LONG']);
                    case NumberFilterType.RANGE:
                        return {
                            '@type': 'AndConstraint',
                            constraints: [
                                this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value[0]), ComparisonConstraint.OperatorEnum.GREATERTHANOREQUAL, 'LONG']),
                                this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value[1]), ComparisonConstraint.OperatorEnum.LESSTHANOREQUAL, 'LONG'])
                            ]
                        } as AndConstraint;
                }
                break;
            case 'boolean':
                return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.EQUALS, 'BOOLEAN']);
            case 'date':
                return this.buildDateConstraint(propertyName, comparatorType.code as DateFilterType, value, options?.excludeYear === true);
            case 'list':
                switch (comparatorType?.code) {
                    case ListFilterType.EMPTY:
                        return this.parseOperatorToExistenceConstraint(propertyName, [PropertyExistenceConstraint.ExistenceOperatorEnum.ISNULL, 'STRING']);
                    case ListFilterType.NOT_EMPTY:
                        return this.parseOperatorToExistenceConstraint(propertyName, [PropertyExistenceConstraint.ExistenceOperatorEnum.ISNOTNULL, 'STRING']);
                }
                // Exactement (ou filtre enregistré avant l'ajout du type)
                return this.parseOperatorToConstraint(propertyName, [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.EQUALS, 'STRING']);
            case 'newsletter':
                return this.handleNewsletterFilter(propertyName, comparatorType, value);
            case 'campaign':
                return this.handleCampaignFilter(propertyName, comparatorType, value, options);
            default:
                break;
        }
    }

    /**
     * Contrainte d'un filtre de date.
     *
     * Sans excludeYear, chaque date désigne une journée entière, en intervalle semi-ouvert
     * (>= jour, < lendemain) : le résultat est juste sur une colonne DATE comme DATETIME.
     * Avec excludeYear, le SQL compare MONTH * 100 + DAY (28/09 => 928) ; une période
     * qui passe le 31/12 est découpée en deux (>= début OU <= fin).
     */
    private buildDateConstraint(propertyName: string, code: DateFilterType, value: unknown, excludeYear: boolean): Constraint {
        const Op = ComparisonConstraint.OperatorEnum;
        if (code === DateFilterType.EMPTY || code === DateFilterType.NOT_EMPTY) {
            // vide ou null, comme pour les chaînes ; l'année n'a pas de sens ici
            return this.parseOperatorToExistenceConstraint(propertyName, [code === DateFilterType.EMPTY
                ? PropertyExistenceConstraint.ExistenceOperatorEnum.ISNULL
                : PropertyExistenceConstraint.ExistenceOperatorEnum.ISNOTNULL, 'STRING']);
        }
        if (code === DateFilterType.SLIDING_RANGE) {
            return this.buildSlidingRangeConstraint(propertyName, value, excludeYear);
        }

        const dates = (Array.isArray(value) ? value : [value]).map(d => this.ensureDateObject(d));
        const date = dates[0];
        if (!date) {
            return null;
        }
        const monthDay = () => String(this.getMonthDay(date));
        switch (code) {
            case DateFilterType.EXACTLY:
                return this.or(dates.filter(d => d != null).map(d => this.buildDayRangeConstraint(propertyName, d, d, excludeYear)));
            case DateFilterType.NOT_EXACTLY:
                // comme NOT_EQUALS côté serveur, une date vide est « différente »
                return this.and(dates.filter(d => d != null).map(d => excludeYear
                    ? this.dateComparison(propertyName, Op.NOTEQUALS, String(this.getMonthDay(d)), true)
                    : this.or([
                        this.dateComparison(propertyName, Op.LESSTHAN, this.toISODate(d)),
                        this.dateComparison(propertyName, Op.GREATERTHANOREQUAL, this.toISODate(this.addDays(d, 1))),
                        this.existence(propertyName, PropertyExistenceConstraint.ExistenceOperatorEnum.ISNULL)
                    ])));
            case DateFilterType.BEFORE:
                return excludeYear
                    ? this.dateComparison(propertyName, Op.LESSTHAN, monthDay(), true)
                    : this.dateComparison(propertyName, Op.LESSTHAN, this.toISODate(date));
            case DateFilterType.BEFORE_EQUALS:
                return excludeYear
                    ? this.dateComparison(propertyName, Op.LESSTHANOREQUAL, monthDay(), true)
                    : this.dateComparison(propertyName, Op.LESSTHAN, this.toISODate(this.addDays(date, 1)));
            case DateFilterType.AFTER:
                return excludeYear
                    ? this.dateComparison(propertyName, Op.GREATERTHAN, monthDay(), true)
                    : this.dateComparison(propertyName, Op.GREATERTHANOREQUAL, this.toISODate(this.addDays(date, 1)));
            case DateFilterType.AFTER_EQUALS:
                return excludeYear
                    ? this.dateComparison(propertyName, Op.GREATERTHANOREQUAL, monthDay(), true)
                    : this.dateComparison(propertyName, Op.GREATERTHANOREQUAL, this.toISODate(date));
            case DateFilterType.RANGE:
                // seule la date de début est choisie : période d'un jour
                return this.buildDayRangeConstraint(propertyName, date, dates[1] ?? date, excludeYear);
        }
        return null;
    }

    /**
     * Période glissante [unité, delta] : les bornes sont calculées par le serveur à chaque exécution,
     * pour qu'une requête sauvegardée (scénario, export...) glisse avec le temps.
     */
    private buildSlidingRangeConstraint(propertyName: string, value: unknown, excludeYear: boolean): Constraint {
        if (!Array.isArray(value) || !value[0] || value[1] == null) {
            return null;
        }
        const unit = value[0] as DeltaDateConstraint.TypeEnum;
        return {
            '@type': 'SlidingDateRangeConstraint',
            operand1: {
                '@type': 'PropertyValue',
                propertyName,
                // en heures, la précision est la seconde : l'option sans l'année ne s'applique pas
                options: {excludeYear: excludeYear && unit !== DeltaDateConstraint.TypeEnum.HOUR}
            } as PropertyValue,
            unit,
            delta: Number(value[1])
        } as SlidingDateRangeConstraint;
    }

    /**
     * Du jour de from au jour de to inclus (l'heure des dates est ignorée).
     */
    private buildDayRangeConstraint(propertyName: string, from: Date, to: Date, excludeYear: boolean): Constraint {
        const Op = ComparisonConstraint.OperatorEnum;
        if (!excludeYear) {
            return this.and([
                this.dateComparison(propertyName, Op.GREATERTHANOREQUAL, this.toISODate(from)),
                this.dateComparison(propertyName, Op.LESSTHAN, this.toISODate(this.addDays(to, 1)))
            ]);
        }
        const days = Math.round((this.startOfDay(to).getTime() - this.startOfDay(from).getTime()) / 86_400_000);
        if (days >= 365) {
            // une année complète ou plus : tous les jours de l'année sont couverts
            return this.existence(propertyName, PropertyExistenceConstraint.ExistenceOperatorEnum.ISNOTNULL);
        }
        const start = this.getMonthDay(from);
        const end = this.getMonthDay(to);
        if (start === end) {
            return this.dateComparison(propertyName, Op.EQUALS, String(start), true);
        }
        const bounds = [
            this.dateComparison(propertyName, Op.GREATERTHANOREQUAL, String(start), true),
            this.dateComparison(propertyName, Op.LESSTHANOREQUAL, String(end), true)
        ];
        // ex. du 20/12 au 10/01 : >= 1220 OU <= 110
        return start < end ? this.and(bounds) : this.or(bounds);
    }

    /**
     * Comparaison sur un champ date : valeur ISO ('2026-09-28' ou '2026-09-28T10:00:00'),
     * ou jour/mois (928) si excludeYear.
     */
    private dateComparison(propertyName: string, operator: ComparisonConstraint.OperatorEnum, value: string, excludeYear = false): ComparisonConstraint {
        return {
            '@type': 'ComparisonConstraint',
            operand1: {
                '@type': 'PropertyValue',
                propertyName,
                options: {excludeYear}
            } as PropertyValue,
            operand2: {
                '@type': 'Value',
                typeValue: excludeYear ? 'LONG' : 'STRING',
                value
            } as Value,
            operator
        } as ComparisonConstraint;
    }

    private existence(propertyName: string, operator: PropertyExistenceConstraint.ExistenceOperatorEnum): PropertyExistenceConstraint {
        return {
            '@type': 'PropertyExistenceConstraint',
            propertyName,
            operator
        } as PropertyExistenceConstraint;
    }

    private and(constraints: Constraint[]): AndConstraint {
        return {'@type': 'AndConstraint', constraints} as AndConstraint;
    }

    private or(constraints: Constraint[]): OrConstraint {
        return {'@type': 'OrConstraint', constraints} as OrConstraint;
    }

    /**
     * Gère les filtres spécifiques aux campagnes (association campagne_contact / campagne_commande)
     *
     * Ce filtre ne repose pas sur un champ de la dataview mais sur une sous-requête
     * vers les tables {@code campaign_association} et {@code campaign}.
     *
     * - Sur une vue contact (table principale = personal) :
     *   EXISTE une ligne dans campaign_association liée au contact
     *   ET campaign_association.id_campaign est lié à une campagne de type (CONTACTS|ORDERS)
     *   avec le flux demandé (IMPORT / EXPORT).
     *
     * - Sur une vue commande (table principale = command) :
     *   même logique mais en reliant campaign_association.id_command à command.id.
     */
    private handleCampaignFilter(propertyName: string, comparatorType: any, value: unknown, options?: any): Constraint {
        const parts = (propertyName || '').split('.');
        const mainTable = parts[0]; // 'personal' | 'command' | ...
        const virtualField = parts.length > 1 ? parts[1] : null;
        const isPersonal = mainTable === 'personal';
        const isCommand = mainTable === 'command';

        if (!isPersonal && !isCommand) {
            // Si on ne sait pas sur quelle vue on est, on ne construit pas de contrainte
            return null;
        }

        const associationTable = 'campaign_association';
        const associationIdField = `${associationTable}.id`;
        const associationLinkField = isPersonal
            ? `${associationTable}.id_personal`
            : `${associationTable}.id_command`;
        const mainIdField = `${mainTable}.id`;

        const dataType = isPersonal ? 'CONTACTS' : 'ORDERS';

        // Sous-requête interne : campagne.id IN (SELECT id FROM campaign WHERE ... [AND data_type = ...] [... autres contraintes ...])
        // Pour les contacts, on ne filtre PAS par data_type afin de récupérer aussi
        // les contacts créés via des campagnes de type ORDERS (commandes).
        //
        // Restreindre par campaign.flux uniquement pour le champ virtuel _campaign_flux : pour code/nom/UTM/statut,
        // comparatorType est un opérateur string (EXACTLY, …) — ne pas interpréter comme type import/export sinon
        // on forçait IMPORT et les campagnes d'export disparaissaient du filtre par code.
        const campaignConstraints: Constraint[] = [];

        if (virtualField === '_campaign_flux') {
            if (comparatorType?.code === CampaignFilterType.EXPORT) {
                campaignConstraints.push({
                    '@type': 'OrConstraint',
                    constraints: [
                        this.parseOperatorToConstraint(
                            'campaign.flux',
                            [this.applyOperatorToString('EXPORT_SCENARIO'), ComparisonConstraint.OperatorEnum.EQUALS, 'STRING']
                        ),
                        this.parseOperatorToConstraint(
                            'campaign.flux',
                            [this.applyOperatorToString('EXPORT_DIRECT'), ComparisonConstraint.OperatorEnum.EQUALS, 'STRING']
                        )
                    ]
                } as OrConstraint);
            } else {
                campaignConstraints.push(
                    this.parseOperatorToConstraint(
                        'campaign.flux',
                        [this.applyOperatorToString('IMPORT'), ComparisonConstraint.OperatorEnum.EQUALS, 'STRING']
                    )
                );
            }
        }

        // Sur les vues commandes uniquement, on restreint aux campagnes de type ORDERS.
        if (isCommand) {
            campaignConstraints.push(
                this.parseOperatorToConstraint(
                    'campaign.data_type',
                    [this.applyOperatorToString(dataType), ComparisonConstraint.OperatorEnum.EQUALS, 'STRING']
                )
            );
        }

        // Filtres supplémentaires selon le champ virtuel (code, name, UTM...)
        if (virtualField === '_campaign_code' && comparatorType?.code) {
            const c = this.getOperatorFromType('campaign.code', 'string', comparatorType, value, null);
            if (c) {
                campaignConstraints.push(c);
            }
        }

        if (virtualField === '_campaign_name' && comparatorType?.code) {
            const c = this.getOperatorFromType('campaign.name', 'string', comparatorType, value, null);
            if (c) {
                campaignConstraints.push(c);
            }
        }

        if (virtualField === '_campaign_utm_source' && comparatorType?.code) {
            const c = this.getOperatorFromType('campaign.utm_source', 'string', comparatorType, value, null);
            if (c) {
                campaignConstraints.push(c);
            }
        }

        if (virtualField === '_campaign_utm_medium' && comparatorType?.code) {
            const c = this.getOperatorFromType('campaign.utm_medium', 'string', comparatorType, value, null);
            if (c) {
                campaignConstraints.push(c);
            }
        }

        if (virtualField === '_campaign_utm_campaign' && comparatorType?.code) {
            const c = this.getOperatorFromType('campaign.utm_campaign', 'string', comparatorType, value, null);
            if (c) {
                campaignConstraints.push(c);
            }
        }

        if (virtualField === '_campaign_utm_term' && comparatorType?.code) {
            const c = this.getOperatorFromType('campaign.utm_term', 'string', comparatorType, value, null);
            if (c) {
                campaignConstraints.push(c);
            }
        }

        if (virtualField === '_campaign_utm_content' && comparatorType?.code) {
            const c = this.getOperatorFromType('campaign.utm_content', 'string', comparatorType, value, null);
            if (c) {
                campaignConstraints.push(c);
            }
        }

        const innerSubQuery: SubQueryConstraint = {
            '@type': 'SubQueryConstraint',
            type: 'IN',
            mainField: `${associationTable}.id_campaign`,
            tableName: 'campaign',
            selectField: 'campaign.id',
            subQueryFilter: {
                constraint: {
                    '@type': 'AndConstraint',
                    constraints: campaignConstraints
                }
            }
        } as SubQueryConstraint;

        // Sous-requête principale : EXISTS (
        //   SELECT campaign_association.id
        //   FROM campaign_association
        //   WHERE campaign_association.id_personal = personal.id
        //     AND campaign_association.id_campaign IN ( ... innerSubQuery ... )
        // )
        // Contraintes côté table d'association (lien + éventuellement statut de contact)
        const associationConstraints: Constraint[] = [
            this.parseOperatorToConstraint(
                associationLinkField,
                [this.applyOperatorToString(mainIdField), ComparisonConstraint.OperatorEnum.EQUALS, 'FIELD']
            )
        ];

        // Filtre sur le statut de campagne (EQUALS) via options.campaignStatus
        const campaignStatus: unknown = options?.campaignStatus;
        if (virtualField === '_campaign_status'
            && campaignStatus != null
            && String(campaignStatus).trim().length > 0) {
            campaignConstraints.push(
                this.parseOperatorToConstraint(
                    'campaign.status',
                    [this.applyOperatorToString(campaignStatus), ComparisonConstraint.OperatorEnum.EQUALS, 'STRING']
                )
            );
        }

        // Filtre sur le statut de contact à l'association (NEW_CONTACT / EXISTING_CONTACT)
        const associationStatus: unknown = options?.associationStatus;
        if (virtualField === '_campaign_contact_status'
            && associationStatus != null
            && String(associationStatus).trim().length > 0) {
            associationConstraints.push(
                this.parseOperatorToConstraint(
                    `${associationTable}.contact_status_at_association`,
                    [this.applyOperatorToString(associationStatus), ComparisonConstraint.OperatorEnum.EQUALS, 'STRING']
                )
            );
        }

        const outerSubQuery: SubQueryConstraint = {
            '@type': 'SubQueryConstraint',
            type: 'EXISTS',
            tableName: associationTable,
            selectField: associationIdField,
            subQueryFilter: {
                constraint: {
                    '@type': 'AndConstraint',
                    constraints: [
                        ...associationConstraints,
                        innerSubQuery
                    ]
                }
            }
        } as SubQueryConstraint;

        return outerSubQuery;
    }

    /** 28 septembre => 928, même calcul que MONTH(x) * 100 + DAY(x) côté SQL */
    private getMonthDay(date: Date): number {
        return (date.getMonth() + 1) * 100 + date.getDate();
    }

    /** Date locale au format 'YYYY-MM-DD' */
    private toISODate(date: Date): string {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
    }

    private addDays(date: Date, days: number): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
    }

    private startOfDay(date: Date): Date {
        return this.addDays(date, 0);
    }

    private parseOperatorToDeltaDateConstraint(propertyName: string, typeDelta: DeltaDateConstraint.TypeEnum, delta: number): DeltaDateConstraint {
        return {
            '@type': 'DeltaDateConstraint',
            delta,
            operand1: {
                '@type': 'PropertyValue',
                propertyName
            } as PropertyValue,
            type: typeDelta
        } as DeltaDateConstraint;
    }

    private getValueFromObjectOrList(value): unknown[] | unknown {
        if (Array.isArray(value)) {
            return value.map(i => this.getValueFromObject(i));
        } else {
            return this.getValueFromObject(value);
        }
    }

    private getValueFromObject(value): unknown {
        if (value?.code) {
            return value.code;
        } else if (value?.key) {
            return value.key;
        } else if (typeof value === 'boolean') { 
            return value
        } else if (value) {
            return value;
        } else {
            return null;
        }
    }

    public buildSortMetas(orderings: Array<FieldOrder>): SortMeta[] {
        if (orderings) {
            return orderings.map(o =>
                ({
                    field: o.code,
                    order: o.asc ? 1 : -1
                } as SortMeta));
        } else {
            return [];
        }
    }


    public buildSortMetasByOrdering(orderings: Array<Ordering>): SortMeta[] {
        if (orderings) {
            return orderings.map(o =>
                ({
                    field: (o.operand as PropertyValue).propertyName,
                    order: o.order === 'ASC' ? 1 : -1
                } as SortMeta));
        } else {
            return [];
        }
    }

    public buildOrderings(orderings: SortMeta[]): Array<Ordering> {
        if (orderings) {
            return orderings.map((order) => ({
                order: order.order === 1 ? 'ASC' : 'DESC',
                operand: {
                    '@type': 'PropertyValue',
                    propertyName: order.field
                }
            } as Ordering));
        } else {
            return [];
        }
    }

    /**
     * Ensures a value is a Date object, converting from string if necessary.
     * Used in query building to handle both Date objects and ISO date strings.
     * Returns null for a missing or invalid value (instead of 01/01/1970).
     */
    private ensureDateObject(value: unknown): Date | null {
        if (value == null || value === '') {
            return null;
        }
        let date: Date;
        if (value instanceof Date) {
            date = value;
        } else {
            // 'YYYY-MM-DD' seul est interprété en UTC par new Date() : on le lit en heure locale
            const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
            date = day ? new Date(+day[1], +day[2] - 1, +day[3]) : new Date(value as string);
        }
        return isNaN(date.getTime()) ? null : date;
    }

    /**
     * Parses an ISO date string to a Date object.
     * Includes type guard to handle cases where value is already a Date.
     */
    private parseISOStringToDate(value: any): Date | null {
        if (value == null) {
            return null;
        }

        // Already a Date object return as-is
        if (value instanceof Date) {
            return value;
        }

        // String attempt to parse
        if (typeof value === 'string') {
            // Check if valid ISO string
            const isoRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3}Z?)?)?$/;
            if (isoRegex.test(value)) {
                const date = new Date(value);
                return isNaN(date.getTime()) ? null : date;
            }
        }

        return null;
    }

    /**
     * Reconstructs Date objects from ISO strings based on filter type.
     * Handles:
     * - Single Date (EXACTLY, BEFORE, AFTER, etc.)
     * - Array of 2 Dates (RANGE)
     * - Array of [unit, delta] (SLIDING_RANGE) - not converted
     */
    private reconstructDateValue(value: any, filterTypeCode: DateFilterType): any {
        // SLIDING_RANGE uses [unit, delta] format, not dates
        if (filterTypeCode === DateFilterType.SLIDING_RANGE) {
            return value; // Don't convert - this is [string, number]
        }

        // RANGE uses array of 2 dates
        if (filterTypeCode === DateFilterType.RANGE) {
            if (Array.isArray(value) && value.length === 2) {
                return [
                    this.parseISOStringToDate(value[0]),
                    this.parseISOStringToDate(value[1])
                ];
            }
            return value;
        }

        // EXACTLY with multiple dates (array)
        if (filterTypeCode === DateFilterType.EXACTLY && Array.isArray(value)) {
            return value.map(v => this.parseISOStringToDate(v));
        }

        // All other date filter types use single date
        return this.parseISOStringToDate(value);
    }

    /**
     * Recursively reconstructs Date objects from ISO strings in a filter tree node.
     * This is necessary because JSON.parse() converts Date objects to strings.
     */
    private reconstructDatesInTreeNode(node: TreeNode<Filter>): TreeNode<Filter> {
        if (!node) {
            return node;
        }

        // Process current node's filter data
        if (node.data && node.data.field) {
            const filter = node.data;

            // Only process date fields
            if (filter.field.variableType === 'date' && filter.value != null && filter.filterType?.code) {
                filter.value = this.reconstructDateValue(filter.value, filter.filterType.code as DateFilterType);
            }
        }

        // Recursively process children
        if (node.children && node.children.length > 0) {
            node.children.forEach(child => this.reconstructDatesInTreeNode(child));
        }

        return node;
    }

    /**
     * Reconstructs Date objects in a ClassicTreeSave value tree.
     * Useful when a ClassicTreeSave has been deep-copied via JSON.parse(JSON.stringify())
     * which converts Date objects to strings.
     */
    public reconstructDatesInClassicTreeSave(save: ClassicTreeSave): ClassicTreeSave {
        if (save && save.value) {
            save.value = this.reconstructDatesInTreeNode(save.value);
        }
        return save;
    }

    public buildClassicTreeSaveBySaveQuery(s: SaveQuery): ClassicTreeSave {
        // Parse JSON to get tree structure
        const parsedTree: TreeNode<Filter> = JSON.parse(s.objectQueryJson);

        // Reconstruct Date objects from ISO strings
        const treeWithDates = this.reconstructDatesInTreeNode(parsedTree);

        return {
            id: s.id,
            name: s.name,
            cols: s.displayables,
            value: treeWithDates,
            orderings: this.buildSortMetas(s.orderings),
            having: s.having || s.query?.havingCountTable || [],
            partner: s.partner,
            affectedPartners: s.affectedPartners,
            isModel: s.isModel
        };
    }

    /**
     * Gère les filtres spécifiques aux newsletters
     */
    private handleNewsletterFilter(propertyName: string, comparatorType: any, value: unknown): Constraint {
        const tableName = 'personal_newsletter';
        const uuidField = `${tableName}.uuid`;
        const statusField = `${tableName}.newsletter_registration_status`;

        switch (comparatorType.code) {
            case NewsletterFilterType.SUBSCRIBED_TO:
                // contact abonné à une newsletter spécifique
                // WHERE personal_newsletter.uuid = 'UUID' AND personal_newsletter.newsletter_registration_status = true
                return {
                    '@type': 'AndConstraint',
                    constraints: [
                        this.parseOperatorToConstraint(
                            uuidField,
                            [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.EQUALS, 'STRING']
                        ),
                        this.parseOperatorToConstraint(
                            statusField,
                            [this.applyOperatorToString(true), ComparisonConstraint.OperatorEnum.EQUALS, 'BOOLEAN']
                        )
                    ]
                } as AndConstraint;
            case NewsletterFilterType.NOT_SUBSCRIBED_TO:
                return {
                    '@type': 'SubQueryConstraint',
                    type: 'NOT_IN',
                    mainField: 'personal.id',
                    tableName: tableName,
                    selectField: `${tableName}.id_personal`,
                    subQueryFilter: {
                        constraint: {
                            '@type': 'AndConstraint',
                            constraints: [
                                this.parseOperatorToConstraint(
                                    `${tableName}.uuid`,
                                    [this.applyOperatorToString(value), ComparisonConstraint.OperatorEnum.EQUALS, 'STRING']
                                ),
                                this.parseOperatorToConstraint(
                                    `${tableName}.newsletter_registration_status`,
                                    [this.applyOperatorToString(true), ComparisonConstraint.OperatorEnum.EQUALS, 'BOOLEAN']
                                )
                            ]
                        }
                    }
                } as SubQueryConstraint;
            case NewsletterFilterType.SUBSCRIBED_TO_ANY:
                // Contact abonné à au moins une newsletter
                return {
                    '@type': 'SubQueryConstraint',
                    type: 'EXISTS',
                    tableName: tableName,
                    selectField: `${tableName}.id`,
                    subQueryFilter: {
                        constraint: {
                            '@type': 'AndConstraint',
                            constraints: [
                                this.parseOperatorToConstraint(
                                    `${tableName}.id_personal`, 
                                    [this.applyOperatorToString('personal.id'), ComparisonConstraint.OperatorEnum.EQUALS, 'FIELD']
                                ),
                                this.parseOperatorToConstraint(
                                    `${tableName}.newsletter_registration_status`,
                                    [this.applyOperatorToString(true), ComparisonConstraint.OperatorEnum.EQUALS, 'BOOLEAN']
                                )
                            ]
                        }
                    }
                } as SubQueryConstraint;

            case NewsletterFilterType.NOT_SUBSCRIBED_TO_ANY:
                // Contact abonné à aucune newsletter
                return {
                    '@type': 'SubQueryConstraint',
                    type: 'NOT_EXISTS',
                    tableName: tableName,
                    selectField: `${tableName}.id`,
                    subQueryFilter: {
                        constraint: {
                            '@type': 'AndConstraint',
                            constraints: [
                                this.parseOperatorToConstraint(
                                    `${tableName}.id_personal`, 
                                    [this.applyOperatorToString('personal.id'), ComparisonConstraint.OperatorEnum.EQUALS, 'FIELD']
                                ),
                                this.parseOperatorToConstraint(
                                    `${tableName}.newsletter_registration_status`,
                                    [this.applyOperatorToString(true), ComparisonConstraint.OperatorEnum.EQUALS, 'BOOLEAN']
                                )
                            ]
                        }
                    }
                } as unknown as SubQueryConstraint;

            default:
                throw new Error(`Type de filtre newsletter non supporté: ${comparatorType.code}`);
        }
    }
}
